import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { debounce, Subject, takeUntil, timer } from 'rxjs';
import {
  BankingInformationDto,
  PersonalService
} from '../../services/personal.service';
import { UserData } from '../../services/user-management.service';
import { RedirectService } from '../../services/redirect.service';

interface BikCheck {
  inn?: number;
  kpp?: string;
  bik?: string;
  ks?: string;
  name?: string;
  logoUrl?: string | null;
}

@Component({
  selector: 'app-banking-information',
  templateUrl: './banking-information.component.html',
  styleUrls: ['./banking-information.component.scss']
})
export class BankingInformationComponent implements OnInit, OnDestroy {
  loginFlag: boolean = false;
  bikIsValid: boolean = false;
  rsIsValid: boolean = false;
  rsErrorDescription: string = '';
  selectedBankData: BikCheck = {};
  currenBankingData: BankingInformationDto[] = [];
  userData?: UserData;

  bankingDataForm: FormGroup = new FormGroup({
    bik: new FormControl('', [
      Validators.required,
      Validators.pattern('^[0-9]{9}$')
    ]),
    rs: new FormControl({ value: '', disabled: true }, [
      Validators.required,
      Validators.pattern('^[0-9]{20}$')
    ])
  });

  private destroy$ = new Subject<void>();

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private personalService: PersonalService,
    private redirectService: RedirectService
  ) {}

  ngOnInit() {
    this.route.queryParams
      .pipe(takeUntil(this.destroy$))
      .subscribe(params => {
        this.loginFlag = params['authPath'] === 'true';
      });

    this.personalService
      .getUserData()
      .pipe(takeUntil(this.destroy$))
      .subscribe(r => {
        if (r.data && r.success) {
          this.userData = r.data;
        }
      });

    this.personalService
      .loadBankingData()
      .pipe(takeUntil(this.destroy$))
      .subscribe(r => {
        if (r.data && r.success) {
          this.currenBankingData = r.data;
          if (r.data.length > 0) {
            this.bankingDataForm.get('rs')?.setValue(r.data[0].rs);
            this.bankingDataForm.get('bik')?.setValue(r.data[0].bik);
          }
        }
      });

    this.bankingDataForm
      .get('bik')
      ?.statusChanges.pipe(
        debounce(() => timer(200)),
        takeUntil(this.destroy$)
      )
      .subscribe(status => {
        if (status === 'VALID') {
          this.checkBik();
        } else {
          this.bikIsValid = false;
          this.bankingDataForm.get('rs')?.disable();
          this.selectedBankData = {};
        }
      });

    this.bankingDataForm
      .get('rs')
      ?.statusChanges.pipe(
        debounce(() => timer(200)),
        takeUntil(this.destroy$)
      )
      .subscribe(status => {
        if (status === 'VALID') {
          this.checkBikAndRs();
        }
      });
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  checkBik() {
    const bikValue = this.bankingDataForm.get('bik')?.value;
    this.personalService
      .checkBik(bikValue)
      .pipe(takeUntil(this.destroy$))
      .subscribe(res => {
        if (res.success && res.data) {
          this.bikIsValid = true;
          this.selectedBankData = { ...res.data };
          this.bankingDataForm.get('rs')?.enable();
        } else {
          this.bikIsValid = false;
          this.bankingDataForm.get('rs')?.disable();
          this.selectedBankData = {};
          this.bankingDataForm.get('bik')?.setErrors({ incorrect: true });
        }
      });
  }

  saveBankingData() {
    const newBankingData = {
      ...this.selectedBankData,
      rs: this.bankingDataForm.controls['rs'].value
    } as BankingInformationDto;
    this.personalService
      .postBankDetails({ details: [newBankingData] })
      .pipe(takeUntil(this.destroy$))
      .subscribe(res => {
        if (res.success) {
          if (this.loginFlag) {
            this.redirectService.checkRedirectPath();
          } else {
            this.close();
          }
          this.rsErrorDescription = '';
        }
        if (res.error_description) {
          this.bankingDataForm
            .get('rs')
            ?.setErrors({ RS_BIK_NOT_UNIQUE: true });
          this.rsErrorDescription = res?.error_description;
        }
      });
  }

  skip() {
    this.router.navigate(['requests']).then();
  }

  close() {
    this.router.navigate(['account']).then();
  }

  private checkBikAndRs() {
    if (this.bankingDataForm.valid && this.bikIsValid) {
      const rsValue = this.bankingDataForm.get('rs')?.value;
      const bikValue = this.bankingDataForm.get('bik')?.value;

      const dataForCheckRS = {
        rs: rsValue,
        bik: bikValue,
        type: this.userData ? this.userData.type : 'SELF_EMPLOYED'
      };

      this.personalService
        .checkBankAccount(dataForCheckRS)
        .pipe(takeUntil(this.destroy$))
        .subscribe(res => {
          if (res.success) {
            this.rsErrorDescription = '';
            this.rsIsValid = true;
          } else {
            this.rsIsValid = false;
            this.rsErrorDescription = res.error_description;
            this.bankingDataForm.get('rs')?.setErrors({ INCORRECT_RS: true });
          }
          this.bankingDataForm.updateValueAndValidity();
        });
    }
  }
}
